import axios from 'axios'
import Questionnaire, { Question, Section } from '../models/questionnaire'
import { QuestionComment, QuestionGroup, QuestionnaireComments, SectionGroup } from '../models/QuestionnaireComments'
import Startup, { StartupUpload, StartupResults } from '../models/startup'
import { UserProfile } from '../models/UserProfile'
import UserScore from '../models/UserScore'

interface FetchParameters {
  limit: number,
  offset: number,
  matching?: string
}

export const fetchStartups = async (token: string, limit: number, offset: number, searchText: string | undefined) : Promise<StartupResults> => {
  const params: FetchParameters = {
    limit: limit,
    offset: offset
  }
  if (searchText) {
    params.matching = searchText
  }
  const results = await axios.get(`${process.env.REACT_APP_API_HOST}/startups`, {
    headers: {
      Authorization: `${token}`
    },
    params: params
  })

  const responseData = results.data
  const count = responseData.total_count
  const startupData = responseData.startups

  const startups = startupData.map((responseObject: any) => {
    return new Startup(
      responseObject.id, 
      responseObject.name, 
      responseObject.description, 
      responseObject.logo_url, 
      new Date(Date.parse(responseObject.created)),
      responseObject.contact_name,
      responseObject.contact_email,
      responseObject.company_website,
      responseObject.company_email,
      responseObject.commercial_viability_score,
      responseObject.user_commercial_viability_score,
      responseObject.company_phone,
      responseObject.deck_url,
      responseObject.video_url
    )
  })

  return {
    total_count: count,
    startups: startups
  }
}

export const getQuestionnaire = async (token: string) => {
  const results = await axios.get(`${process.env.REACT_APP_API_HOST}/commercial-viability`, {
    headers: {
      Authorization: `${token}`
    }
  })

  return new Questionnaire(results.data)
}

export const submitQuestionnaire = async (token: string, startupId: string, questionnaire: Questionnaire) => {
  const body = {
    answers: questionnaire.selectedAnswers(),
    overall_confidence: questionnaire.overallConfidence,
    overall_comment: questionnaire.overallComments,
    pitch_deck_quality: questionnaire.pitchDeckQuality
  }

  return await axios.post(`${process.env.REACT_APP_API_HOST}/startups/${startupId}/commercial-viability`, body, {
    headers: {
      Authorization: `${token}`
    }
  })
}

export const fetchStartupDetails = async (token: string, startupId: string) => {
  const result = await axios.get(`${process.env.REACT_APP_API_HOST}/startups/${startupId}`, {
    headers: {
      Authorization: `${token}`
    }
  })
  const responseObject = result.data
  return new Startup(
    responseObject.id,
    responseObject.name,
    responseObject.description,
    responseObject.logo_url,
    new Date(Date.parse(responseObject.created)),
    responseObject.contact_name,
    responseObject.contact_email,
    responseObject.company_website,
    responseObject.company_email,
    responseObject.commercial_viability_score,
    responseObject.user_commercial_viability_score,
    responseObject.company_phone,
    responseObject.deck_url,
    responseObject.video_url,
    responseObject.naics
  )
}

export const getUploadLink = async (token: string, file: any) => {
  const result = await axios.get(`${process.env.REACT_APP_API_HOST}/files/upload`, {
    headers: {
      Authorization: `${token}`
    },
    params: {
      file_type: file.type,
      object_key: file.name
    }
  })

  let url = result.data.url
  if (url) {
    url = url.replace('\\', '')
  }
  return url
}

export const uploadFile = async (path: string, file: any) => {
  const result = await axios.put(path, file)
  return result
}

export const getdownloadLink = async (token: string, path: string) => {
  let strippedPath = path
  while (strippedPath.charAt(0) === '/') {
    strippedPath = strippedPath.substring(1)
  }
  const result = await axios.get(`${process.env.REACT_APP_API_HOST}/files/download`, {
    headers: {
      Authorization: `${token}`
    },
    params: {
      object_key: strippedPath
    }
  })
  return result.data.url
}

export const createStartup = async (token: string, startup: StartupUpload) => {
  const result = await axios.post(`${process.env.REACT_APP_API_HOST}/startups`, startup, {
    headers: {
      Authorization: `${token}`
    }
  })
  return result.data
}

export const getProfile = async (token: string) => {
  const result = await axios.get(`${process.env.REACT_APP_API_HOST}/profile`, {
    headers: {
      Authorization: `${token}`
    }
  })
  return result.data
}

export const updateProfile = async (token: string, profile: UserProfile) => {
  const result = await axios.put(`${process.env.REACT_APP_API_HOST}/profile`, profile, {
    headers: {
      Authorization: `${token}`
    }
  })
  return result.data
}

export const updateStartup = async (token: string, startupId: string, startup: StartupUpload) => {
  const result = await axios.put(`${process.env.REACT_APP_API_HOST}/startups/${startupId}`, startup, {
    headers: {
      Authorization: `${token}`
    }
  })
  return result.data
}

export const fetchAllUserScoresForStartup = async (token: string, startupId: string) : Promise<UserScore[]> => {
  const result = await axios.get(`${process.env.REACT_APP_API_HOST}/startups/${startupId}/commercial-viability/scores/all`, {
    headers: {
      Authorization: `${token}`
    }
  })
  return result.data
}

export const inviteUser = async (token: string, email: string, role: string) => {
  const result = await axios.post(`${process.env.REACT_APP_API_HOST}/users/invite`, {
    email: email,
    role: role
  }, {
    headers: {
      Authorization: `${token}`
    }
  })
  return result.data
}

export const fetchCommentsForStartup = async (token: string, startupId: string) : Promise<QuestionnaireComments> => {
  const questionnaire = await getQuestionnaire(token)
  const result = await axios.get(`${process.env.REACT_APP_API_HOST}/startups/${startupId}/comments`, {
    headers: {
      Authorization: `${token}`
    }
  })

  const resultData = result.data

  const overall = resultData.overall_comments
  const sectionGroups: SectionGroup[] = []
  questionnaire.sections.forEach((section: Section) => {
    const sectionGroup: SectionGroup = {
      display_text: section.displayText,
      questions: []
    }
    const questionGroups: QuestionGroup[] = []
    section.questions.forEach((question: Question) => {
      const questionGroup: QuestionGroup = {
        display_text: question.displayText,
        comments: []
      }
      resultData.question_comments.forEach((comment: QuestionComment) => {
        if (comment.question_id === question.id) {
          questionGroup.comments.push(comment)
        }
      })
      questionGroups.push(questionGroup)
    })
    sectionGroup.questions = questionGroups
    sectionGroups.push(sectionGroup)
  })
  return {
    overall_comments: overall,
    sections: sectionGroups
  }
}

export const redeemInvitation = async (token: string, invitation: string) => {
  const result = await axios.post(`${process.env.REACT_APP_API_HOST}/invitations`, {
    invitation: invitation
  }, {
    headers: {
      Authorization: `${token}`
    }
  })
  return result.data
}
